import { request } from "../../../services/api"
import type { BaseApiResponse } from "../../../types/api.ts"
import type {
  ProjectMilestoneData,
  ProjectMilestonePatchData,
  ProjectMilestoneResponse,
  ProjectMilestoneSnapshotResponse,
  ProjectMilestoneUpdateData,
  ProjectMilestonesFilterOptions,
  ProjectMilestonesFilterOptionsParams,
  ProjectMilestonesParams,
  ProjectMilestonesReorderData,
  ProjectMilestonesResponse
} from "./services.types.ts"
import { afterStatusWrite } from "../../StatusAutomation/helpers/automatic-move-events.ts"

const BASE_PHASES_URL = `/project-management/project-phases`

const PATHS = {
  getProjectMilestones: `${BASE_PHASES_URL}/`,
  createProjectMilestone: `${BASE_PHASES_URL}/`,
  getProjectMilestonesFilterOptions: `${BASE_PHASES_URL}/filter_options/`,
  getProjectMilestone: (id: string) => `${BASE_PHASES_URL}/${id}/`,
  updateProjectMilestone: (id: string) => `${BASE_PHASES_URL}/${id}/`,
  patchProjectMilestone: (id: string) => `${BASE_PHASES_URL}/${id}/`,
  deleteProjectMilestone: (id: string) => `${BASE_PHASES_URL}/${id}/`,
  reorderMilestones: `${BASE_PHASES_URL}/bulk-reorder/`,
  getProjectMilestoneSnapshot: (id: string) =>
    `/project-management/projects/${id}/progress/`
}

export const getProjectMilestones = async (params: ProjectMilestonesParams) => {
  return request.api.get<ProjectMilestonesResponse, BaseApiResponse<unknown>>(
    PATHS.getProjectMilestones,
    params
  )
}

export const createProjectMilestone = async (data: ProjectMilestoneData) => {
  return request.api.post<
    BaseApiResponse<ProjectMilestoneResponse>,
    BaseApiResponse<unknown>
  >(PATHS.createProjectMilestone, data)
}

export const getProjectMilestonesFilterOptions = async (
  params: ProjectMilestonesFilterOptionsParams
) => {
  return request.api.get<
    ProjectMilestonesFilterOptions,
    BaseApiResponse<unknown>
  >(PATHS.getProjectMilestonesFilterOptions, params)
}

export const getProjectMilestone = async (id: string) => {
  return request.api.get<ProjectMilestoneResponse, BaseApiResponse<unknown>>(
    PATHS.getProjectMilestone(id)
  )
}

const putProjectMilestone = async (
  id: string,
  data: ProjectMilestoneUpdateData
) => {
  return request.api.put<
    BaseApiResponse<ProjectMilestoneResponse>,
    BaseApiResponse<unknown>
  >(PATHS.updateProjectMilestone(id), data)
}

const patchProjectMilestoneRequest = async ({
  id,
  ...data
}: ProjectMilestonePatchData) => {
  return request.api.patch<
    BaseApiResponse<ProjectMilestoneResponse>,
    BaseApiResponse<unknown>
  >(PATHS.patchProjectMilestone(id), data)
}

export const deleteProjectMilestone = async (id: string) => {
  return request.api.delete<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.deleteProjectMilestone(id)
  )
}

export const reorderProjectMilestones = async (
  data: ProjectMilestonesReorderData
) => {
  return request.api.post<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.reorderMilestones,
    data
  )
}

export const getProjectMilestoneSnapshot = async (id: string) => {
  return request.api.get<
    ProjectMilestoneSnapshotResponse,
    BaseApiResponse<unknown>
  >(PATHS.getProjectMilestoneSnapshot(id))
}

// A milestone's status (In Progress, Completed) can set a project rule off.
export const updateProjectMilestone = async (
  id: string,
  data: ProjectMilestoneUpdateData
) => {
  const response = putProjectMilestone(id, data)
  return "status" in (data ?? {}) ? afterStatusWrite(response) : response
}

export const patchProjectMilestone = async (data: ProjectMilestonePatchData) => {
  const response = patchProjectMilestoneRequest(data)
  return "status" in data ? afterStatusWrite(response) : response
}
